import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDate, formatTime, formatPrice } from '../lib/utils';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import { PageLoader } from '../components/common/LoadingSpinner';
import type { Appointment } from '../types';

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('id');

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadAppointment() {
      if (!appointmentId) {
        setError('No appointment ID provided');
        setLoading(false);
        return;
      }

      try {
        const res = await api.getAppointment(appointmentId);
        setAppointment(res.appointment);
      } catch (err) {
        setError('Failed to load appointment details');
      } finally {
        setLoading(false);
      }
    }

    loadAppointment();
  }, [appointmentId]);

  if (loading) return <PageLoader />;

  if (error || !appointment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Something went wrong</h1>
          <p className="mt-2 text-gray-600">{error || 'Unable to find your booking'}</p>
          <div className="mt-6">
            <Link to="/book">
              <Button variant="primary">Try booking again</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="mt-6 text-2xl font-display font-bold text-gray-900">
            Booking Confirmed!
          </h1>
          <p className="mt-2 text-gray-600">
            Thank you for booking with Anvayee Consulting. A confirmation email has been sent to{' '}
            <span className="font-medium">{appointment.customer_email}</span>
          </p>

          {/* Booking Details */}
          <div className="mt-8 bg-gray-50 rounded-lg p-6 text-left">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h2>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Service</span>
                <span className="font-medium text-gray-900">{appointment.service_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Category</span>
                <span className="text-gray-900">{appointment.category_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date</span>
                <span className="font-medium text-gray-900">{formatDate(appointment.appointment_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time</span>
                <span className="font-medium text-gray-900">
                  {formatTime(appointment.appointment_time)} ({appointment.timezone})
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Duration</span>
                <span className="text-gray-900">{appointment.duration_minutes} minutes</span>
              </div>
              {appointment.price_inr && (
                <>
                  <hr className="border-gray-200" />
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="font-bold text-green-600">{formatPrice(appointment.price_inr)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status Badge */}
          <div className="mt-6">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              appointment.status === 'confirmed'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {appointment.status === 'confirmed' ? 'Confirmed' : 'Pending Confirmation'}
            </span>
          </div>

          {/* What's Next */}
          <div className="mt-8 text-left bg-primary-50 rounded-lg p-6">
            <h3 className="font-semibold text-primary-900">What's Next?</h3>
            <ul className="mt-3 space-y-2 text-sm text-primary-800">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Check your email for confirmation and calendar invite
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Meeting link will be sent before your appointment
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-primary-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Prepare any questions or documents you'd like to discuss
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button variant="secondary">Return to Home</Button>
            </Link>
            <Link to="/book">
              <Button variant="primary">Book Another Consultation</Button>
            </Link>
          </div>

          {/* Contact Info */}
          <p className="mt-8 text-sm text-gray-500">
            Need to reschedule? Contact us at{' '}
            <a href="mailto:contact@anvayee.com" className="text-primary-600 hover:underline">
              contact@anvayee.com
            </a>
          </p>
        </Card>
      </div>
    </div>
  );
}
